import { h, Component, Host, Element, Prop, Watch, State, Listen, Event, EventEmitter } from "@stencil/core";
import {
  StageContentPosition,
  StageSchemeName,
  StageSchemeDefinition,
  StageContentWidth,
  StageContentIndicatorColor,
  StageVideoControlColor,
  StageLocalization
} from "./constants/stage-types";
import {
  STAGE_SCHEME_CONFIG,
  STAGE_HEADLINE_SIZES,
  STAGE_SCHEME_MAP,
  STAGE_INDICATOR_COLOR_LIGHT,
  STAGE_VIDEO_CONTROL_COLOR_LIGHT
} from "./constants/stage-constants";
import { applySchemeDelegate, setCssVariablesDelegate, ColorableComponent } from "../../common/colorable-component";
import { isMobile } from "../../utils/screen-helper";
import { ASPECTRATIO_2X1, ASPECTRATIO_1X1 } from "../../common/constants";
import { clampHeadlineSizeTo } from "../../utils/headline-helper";
import { getFirstNestedChild, getNestedChildrenOf } from "../../utils/dom-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { HeadlineTag } from "../headline/constants/headline-types";
import { StageSchemeConfiguration } from "../../constants/colors";
import {
  STAGE_ALIGNMENT,
  STAGE_DEFAULT_ALIGNMENT,
  STAGE_DEFAULT_CONTENT_WIDTH,
  STAGE_DEFAULT_VIOLATOR_HORIZONTAL_POSITION,
  StageAlignment,
  StageViolatorHorizontalPosition
} from "../../constants/global-constants";
import { scrollToElement } from "../../utils/scroll-helper";
import { STAGE_LOCALIZATION } from "./constants/stage-i18n";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { stageSvgs } from "./icon-svgs";

/**
 * @slot headline - Holds a single eon-ui-headline element. Use headline sizes between h1 and h3. Other sizes are clamped automatically to ensure brand compliance.
 * @slot image - Holds a single eon-ui-image element
 * @slot ctas - Holds one or two eon-ui-link and/or eon-ui-button elements.
 */
@Component({
  tag: "eon-ui-stage",
  styleUrl: "stage.scss",
  assetsDirs: ["assets-stage"],
  shadow: true
})
export class Stage
  implements
    ColorableComponent<StageSchemeConfiguration<StageSchemeDefinition>>,
    LocalizableComponent<StageLocalization> {
  schemeConfig: StageSchemeConfiguration<StageSchemeDefinition> = STAGE_SCHEME_CONFIG;
  schemeMap = STAGE_SCHEME_MAP;

  localization: Localization<StageLocalization> = STAGE_LOCALIZATION;
  currentLocalization: StageLocalization;

  @State() activeScheme: StageSchemeDefinition;
  @State() backgroundVideo;
  @State() allowContentAlignment: boolean = false;
  @State() heightAdjustment;
  @State() isPlaying: boolean = true;

  @Element() hostElement: HTMLEonUiStageElement;

  private headline: HTMLEonUiHeadlineElement;
  private video: HTMLEonUiVideoplayerElement;
  private headlineShadow = false;
  private violatorSize: string;
  private buttons: Array<HTMLEonUiButtonElement>;
  private links: Array<HTMLEonUiLinkElement>;

  /**
   * Raised when the component has been loaded.
   */
  @Event() stageLoad: EventEmitter;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: StageSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Enable the colored Contentbox.<br>That means that Headline, Subheadline and other slotted Components have a colored background. The background is dependent from the chosen Scheme<br>
   * E.g.  Scheme "red" enabled the red background, if this property is set to false the scheme "red" ensures that color of the headline and subheadline is red.
   */
  @Prop({ reflect: true }) enableContentOnBackground: boolean = false;

  /**
   * Enable a blurred background for the Contentbox. It <b>only</b> works if the enable-content-on-background-property is true!
   */
  @Prop({ reflect: true }) enableBlurredBackground: boolean = false;

  /**
   * Defines where the content is shown inside the stage
   * @example <eon-ui-stage content-position="center"></eon-ui-stage>
   */
  @Prop() contentPosition: StageContentPosition;

  /**
   * Defines the width of the content is shown inside the stage.<br> - "wide" corresponds to 100%,<br> - "normal" to 75%,<br> - narrow to 50%
   * @default normal
   * @example <eon-ui-stage content-width="normal"></eon-ui-stage>
   */
  @Prop({ mutable: true }) contentWidth: StageContentWidth = STAGE_DEFAULT_CONTENT_WIDTH;

  /**
   * Alignment of the content-box.
   */
  @Prop() alignment: StageAlignment = STAGE_DEFAULT_ALIGNMENT;

  /**
   * Defines the text inside the sub headline
   * @example <eon-ui-stage subheadline-text="this is a subheadline"></eon-ui-stage>
   */
  @Prop({ mutable: true }) subheadlineText: string;

  /**
   * Defines the height of the header of the page where the stage is used. The height of the stage is calculated depending onthis height since
   * both the header and the stage together should take up to 80% of the hight of the viewport. The client should find that value dynamically
   * and pass it down.
   * @example <eon-ui-stage header-height="148"></eon-ui-stage>
   */
  @Prop() headerHeight: number;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for the name- and info-properties,<br>
   *   the HTML-Tag for the info (subheadline) is set automatically depending on this property (headline-html-tag  -1 ), e.g <br>
   *    name(Headline) is H2, then info(subheadline) is H3, name(Headline) is H4, then info(subheadline) is H5, and so on
   *
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Defines the image width and height of the stage to be scaled across the viewport.
   * @example <eon-ui-stage full-height=true></eon-ui-stage>
   */
  @Prop() fullHeight: boolean;

  /**
   * Defines Color of full height indicator
   * @example <eon-ui-stage indicator-color="light"></eon-ui-stage>
   */
  @Prop({ reflect: true }) indicatorColor: StageContentIndicatorColor = STAGE_INDICATOR_COLOR_LIGHT;

  /*
   * Check if violator is available for image
   */
  @State() hasViolator: boolean;

  /**
   * Defines horizontal position of the violator
   */
  // prettier-ignore
  @Prop({ mutable: true }) violatorHorizontalPosition: StageViolatorHorizontalPosition = STAGE_DEFAULT_VIOLATOR_HORIZONTAL_POSITION;

  /**
   * Defines color of pause/resume icon and text
   * @example <eon-ui-stage video-control-color="light"></eon-ui-stage>
   */
  @Prop({ reflect: true }) videoControlColor: StageVideoControlColor = STAGE_VIDEO_CONTROL_COLOR_LIGHT;

  /**
   * Enables the playback option for the video. This property will only be used when video is set to the background.
   */
  @Prop() videoPlaybackOption: boolean = false;

  componentWillLoad() {
    this.headline = getFirstNestedChild(this.hostElement, "eon-ui-headline");
    this.buttons = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-button"));
    this.links = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-link"));
    this.hasViolator = !!getFirstNestedChild(this.hostElement, "eon-ui-violator");
    if (this.hasViolator) {
      this.violatorSize = (getFirstNestedChild(this.hostElement, "eon-ui-violator") as HTMLElement).getAttribute(
        "size"
      );
    }
    this.contentAlignment();
    this.applyScheme();
    this.setCurrentLocalization();
    this.setImageRatio();
    this.video = getFirstNestedChild(this.hostElement, "eon-ui-videoplayer");
    if (this.video) {
      this.setVideoProperties();
    }
    this.video && this.enterViewport(this.hostElement);
    if (this.headline) {
      const size = clampHeadlineSizeTo(STAGE_HEADLINE_SIZES, this.headline.size);
      if (size) this.headline.setAttribute("size", size);
      //run HeadlineAlignment
      this.headlineAlignment();
      if (!this.enableContentOnBackground) {
        this.headlineShadow = this.headline.textShadow;
      } else {
        this.headline.removeAttribute("text-shadow");
      }
      this.handleHeadlineTag();
    }
    this.contentWidth = this.contentWidth ? this.contentWidth : "normal";
    this.updateProcessedText();
    this.fullHeight && this.handleHeightCalculation();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.stageLoad.emit();
  }

  /**
   * This function is calculating the Stage height
   * On basis of whether Navigation or ThirdlevelNavigation is present
   * And then summation value is assigned to "header-height" property for final Stage height calculation.
   */
  @Listen("resize", { target: "window" })
  @Watch("fullHeight")
  async handleHeightCalculation() {
    if (!this.headerHeight) {
      const navigationHeight = await document.getElementsByTagName("eon-ui-navigation")[0]?.updateNavigationHeight();
      const thirdLevelNavigationHeight = await document
        .getElementsByTagName("eon-ui-third-level-navigation")[0]
        ?.updateHeight();
      const bottomDrawerHeight = await document.getElementsByTagName("eon-ui-bottom-drawer")[0]?.getHeight();

      this.heightAdjustment =
        (navigationHeight ? navigationHeight : 0) +
        (thirdLevelNavigationHeight ? thirdLevelNavigationHeight : 0) +
        (bottomDrawerHeight ? bottomDrawerHeight : 0);

      if (Number.isInteger(this.heightAdjustment)) {
        this.hostElement.style.setProperty("--header-height", `${this.heightAdjustment}px`);
      } else {
        this.hostElement.style.setProperty("--header-height", "0px");
      }
    }
  }

  @Watch("headerHeight")
  updateHeaderHeightCSSVariable() {
    if (Number.isInteger(this.headerHeight)) {
      this.hostElement.style.setProperty("--header-height", `${this.headerHeight}px`);
    } else {
      this.hostElement.style.setProperty("--header-height", "0px");
    }
  }

  @Watch("enableContentOnBackground")
  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
    this.setSlottedComponentSchemes();
  }

  /**
   * Set slotted component schemes if no background is enabled.
   * If content background is active, the schemes will stick to background mapping or defined content on background schemes.
   */
  setSlottedComponentSchemes() {
    if (this.headline) {
      let relevantScheme = this.enableContentOnBackground
        ? this.activeScheme.contentBoxHeadlineScheme
        : this.activeScheme.headlineScheme;

      this.headline.setAttribute("scheme", relevantScheme);
    }

    if (this.subheadlineText) {
      let relevantScheme = this.enableContentOnBackground
        ? this.activeScheme.contentBoxSubheadlineColor
        : this.activeScheme.subheadlineColor;

      this.hostElement.style.setProperty("--subheadlineColor", `var(--${relevantScheme})`);
    }

    if (this.buttons) {
      let relevantScheme = this.enableContentOnBackground
        ? this.activeScheme.contentBoxButtonScheme
        : this.activeScheme.buttonScheme;

      this.buttons.forEach((button) => {
        button.setAttribute("scheme", relevantScheme);
      });
    }

    if (this.links) {
      let relevantScheme = this.enableContentOnBackground
        ? this.activeScheme.contentBoxLinkScheme
        : this.activeScheme.linkScheme;

      this.links.forEach((link) => {
        link.setAttribute("scheme", relevantScheme);
      });
    }
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.contentBoxBackgroundColor;
  }

  /**
   * Executes whenever the text property changes. Replaced the specified linewrap symbol with <br />.
   */
  @Watch("subheadlineText")
  updateProcessedText(): void {
    const slashN = "\\n";
    this.subheadlineText = this.subheadlineText && this.subheadlineText.split(slashN).join("<br />");
  }

  @Listen("resize", { target: "window" })
  private setImageRatio() {
    const aspectRatio = isMobile() ? ASPECTRATIO_1X1 : ASPECTRATIO_2X1;
    const imageComponents = this.hostElement.getElementsByTagName("eon-ui-image");
    if (imageComponents?.length > 0) {
      const image = imageComponents[0];
      image.setAttribute("aspect-ratio", aspectRatio);
      image.setAttribute("use-as-background", "true");
    }
  }

  handleIndicatorClick() {
    scrollToElement(this.hostElement, {
      targetPosition: "end",
      offset:
        this.hostElement.offsetHeight +
        (Number.isInteger(this.heightAdjustment)
          ? this.heightAdjustment
          : window.innerHeight - this.hostElement.offsetHeight)
    });
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    if (!!this.headlineHtmlTag) {
      if (this.headline.getAttribute("slot") === "headline") {
        this.headline.setAttribute("headline-html-tag", this.headlineHtmlTag);
      }
    }
  }

  @Watch("alignment")
  contentAlignment() {
    this.allowContentAlignment = (STAGE_ALIGNMENT as ReadonlyArray<string>).includes(this.alignment);
  }

  @Watch("alignment")
  headlineAlignment() {
    if (this.headline && this.allowContentAlignment) {
      this.headline.setAttribute("alignment", this.alignment);
    } else {
      this.headline.setAttribute("alignment", "right");
    }
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  private setVideoProperties() {
    this.backgroundVideo = Array.from(this.hostElement.children).filter.call(
      this.hostElement.children,
      function (child: HTMLElement) {
        return child.tagName === "EON-UI-VIDEOPLAYER";
      }
    );
    this.backgroundVideo.forEach((backgroundVideo) => {
      backgroundVideo.setAttribute("use-as-background", "true");
    });
  }

  handlePlayToggle() {
    this.isPlaying ? this.video.pausePlayback() : this.video.startPlayback();
    this.isPlaying = !this.isPlaying;
  }

  private enterViewport(element: HTMLElement) {
    if (window && window.IntersectionObserver) {
      const videoObserver = new window.IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              this.isPlaying = true;
            } else {
              this.isPlaying = false;
            }
          });
        },
        {
          threshold: [0, 1]
        }
      );
      videoObserver.observe(element);
    }
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            stage: true,
            [this.contentPosition]: !!this.contentPosition,
            ["full-height"]: !!this.fullHeight,
            "has-violator": !!this.hasViolator,
            [`violator-${this.violatorSize}`]: !!this.violatorSize
          })}
        >
          {this.video && (
            <div class={pfx("stage-video")}>
              <slot name="video"></slot>
            </div>
          )}
          <div class={pfx("stage-image")}>
            <slot name="image"></slot>
          </div>
          <div
            class={pfx({
              ["content-wrapper"]: true
            })}
          >
            {this.hasViolator && (
              <div
                class={pfx({
                  "stage-violator": true,
                  [this.violatorHorizontalPosition]: true
                })}
              >
                <slot name="violator"></slot>
              </div>
            )}
            <div
              class={pfx({
                ["stage-content"]: true,
                [this.contentWidth]: !!this.contentWidth,
                ["has-colored-background"]: this.enableContentOnBackground,
                ["is-blurred-background"]: this.enableBlurredBackground && this.enableContentOnBackground,
                [this.alignment]: this.allowContentAlignment
              })}
            >
              <div class={pfx("stage-headline")}>
                <slot name="headline"></slot>
              </div>

              {this.subheadlineText && (
                <span
                  class={pfx({
                    ["subheadline"]: true,
                    ["shadow"]: this.headlineShadow,
                    [this.alignment]: this.allowContentAlignment
                  })}
                  innerHTML={this.subheadlineText}
                ></span>
              )}
              <div class={pfx("stage-ctas")}>
                <slot name="cta"></slot>
              </div>
              {this.video && this.videoPlaybackOption && (
                <button
                  tabindex="0"
                  class={pfx({
                    ["icon-text-wrapper"]: true,
                    [`video-control-${this.videoControlColor}`]: this.videoControlColor
                  })}
                  onClick={() => this.handlePlayToggle()}
                >
                  <div class={pfx("icon-wrapper")}>{this.isPlaying ? stageSvgs.pause() : stageSvgs.play()}</div>
                  <div class={pfx("text-wrapper")}>
                    <span class={pfx("text")}>
                      {this.isPlaying ? this.currentLocalization.play : this.currentLocalization.pause}
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>
          {this.fullHeight && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              class={pfx({
                ["full-height-indicator-wrapper"]: true
              })}
              role="button"
              tabIndex={0}
              onClick={() => this.handleIndicatorClick()}
            >
              <svg
                class={pfx({
                  ["indicator-svg"]: true,
                  [`indicator-${this.indicatorColor}`]: this.indicatorColor
                })}
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 49 48"
              >
                <path
                  class={pfx({
                    ["indicator-path"]: true
                  })}
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M3.10826 6.55155C0.962402 9.96667 0.962402 14.6444 0.962402 24C0.962402 33.3556 0.962402 38.0333 3.10826 41.4484C4.22725 43.2293 5.7331 44.7352 7.51395 45.8541C10.9291 48 15.6068 48 24.9624 48C34.318 48 38.9957 48 42.4109 45.8541C44.1917 44.7352 45.6976 43.2293 46.8165 41.4484C48.9624 38.0333 48.9624 33.3556 48.9624 24C48.9624 14.6444 48.9624 9.96667 46.8165 6.55155C45.6976 4.7707 44.1917 3.26485 42.4109 2.14586C38.9957 0 34.318 0 24.9624 0C15.6068 0 10.9291 0 7.51395 2.14586C5.7331 3.26485 4.22725 4.7707 3.10826 6.55155ZM30.1695 25.7071C30.56 25.3166 30.56 24.6834 30.1695 24.2929C29.779 23.9024 29.1458 23.9024 28.7553 24.2929L25.9624 27.0858V18.5C25.9624 17.9477 25.5147 17.5 24.9624 17.5C24.4101 17.5 23.9624 17.9477 23.9624 18.5V27.0858L21.1695 24.2929C20.779 23.9024 20.1458 23.9024 19.7553 24.2929C19.3648 24.6834 19.3648 25.3166 19.7553 25.7071L24.2553 30.2071C24.6458 30.5976 25.279 30.5976 25.6695 30.2071L30.1695 25.7071Z"
                />
              </svg>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
